import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {choose} from 'lit/directives/choose.js';
import {when} from 'lit/directives/when.js';
import {customElement} from 'lit/decorators/custom-element.js';
import store from '../../store';
import {conditional} from '../../util/conditional';
import {luminaryOptions} from '../../sectionConfig';
import {getProductData} from '../../util/getProductData';
import {filterAndJoin} from '../../util/filterAndJoin';
import {getValueOfFeature, hasFeatureValue, pimText, pimTextDebug} from '../../util/values';
import {
	selectCRI,
	selectDimensionHeight, selectDimensionWidth, selectDimensionDepth, selectDimensionDiameter, selectProductModelId,
	selectWeight,
	selectWindsurfacearea
} from '../../selectors';

@customElement('shared-technical-data')
export class SharedTechnicalData extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0 var(--specsheet-large-gap) var(--specsheet-medium-gap) var(--specsheet-large-gap);
				display: flex;
				flex-direction: column;
			}

			@media print {
				:host {
					margin: 0;
				}

				.drawing {
					width: 30%;
				}

				.dataList {
					width: 70%;
				}

				img {
					max-width: 100%;
				}
			}
        `]
	;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.featuremodel = state.featureModel;
		this.productData = state.productData;
		this.status = state.status;
		this.lang = state.status.lang;
		this.lightProfile = state.lightProfile.data;
		this.decisions = state.decisions.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.state.decisions.data, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.state.decisions.data, this.state.productData.translations, value, prefix, suffix);
	}
	c(id) {
		return conditional(this.decisions, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		const lightEngineDescription = this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV');
		const opticalVersion = this.t('LE_OpticalVersion_1', 'FV.S');
		const reflectorColour = this.t('LE_ReflectorColour_1', 'FV.S');
		const isLightEngineDescriptionPresent = lightEngineDescription || opticalVersion || reflectorColour;

		const controlNoWire = this.t('EB_ControlNoWire_1', 'FV.S');
		const controlWire = this.t('EB_ControlWire_1', 'FV.S');
		const isControlPresent = controlNoWire || controlWire;

		const dimensionsLabel = this.t('TE_Dimensions', 'D');
		const dimensionDiameter = selectDimensionDiameter(s);
		const dimensionDepth = selectDimensionDepth(s);
		const dimensionWidth = selectDimensionWidth(s);
		const dimensionHeight = selectDimensionHeight(s);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;

		const applicableStandards = [
			{ key: 'LU_IP.IP66', labelContext: 'DV.S', valueContext: 'DV.S' },
			{ key: 'LU_IK.10', labelContext: 'DV.S', valueContext: 'DV.S' },
			{ key: 'EB_ElectricalProtectionClass_1', labelContext: 'F', valueContext: 'FV.S' },
			{ key: 'LU_ENEC_CertificateDescription.ENEC', labelContext: 'DV.S', valueContext: 'DV.S' },
			{ key: 'LU_CE.CE', labelContext: 'DV.S', valueContext: 'DV.S' }
		];
		const aggregatedStandards = filterAndJoin(applicableStandards.map(data => this.t(data.key, data.valueContext)), ', ');
		const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		const isStandardsPresent = filteredStandards.length > 0;

		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');
		const wattageMax = this.c('REDTAB.RETURN_EB_Wattage_Max');



		return html`
					<shared-specsheet-feature>
						<trans-label slot="label" class="label" key="HS_HousingSize" context="F"></trans-label>
						<span slot="value" class="value">
						${filterAndJoin([
							this.t('HS_HousingSize', 'FV.S'),
							this.t('LU_LuminaireModel.PoleLateral', 'DV.S'),
							this.t('HS_CircularBaseProfile', 'FV.S', null, ', '),
						], ' ')}
						</span>
					</shared-specsheet-feature>

					<shared-specsheet-feature condition="${this.t('LU_LuminaireModel.PoleLateral', 'D')}">
        			    <trans-label slot="label" class="label" key="LU_LuminaireModel.PoleLateral" context="D"></trans-label>
        			    <trans-label slot="value" class="value" key="LU_LuminaireModel.PoleLateral" context="DV.S"></trans-label>
        			</shared-specsheet-feature>

					<shared-specsheet-feature  condition="${this.t('LE_ColourTemperature_1', 'F')}">
						<trans-label slot="label" class="label" key="LE_ColourTemperature_1" context="F"></trans-label>
						<trans-label slot="value" class="value" key="LE_ColourTemperature_1" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature  condition="${this.t('LE_DimmingLevel_1', 'F')}">
					    <trans-label slot="label" class="label" key="LE_DimmingLevel_1" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="LE_DimmingLevel_1" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('HS_LightEmission', 'F')}">
					    <trans-label slot="label" class="label" key="HS_LightEmission" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="HS_LightEmission" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('HS_Base', 'F')}">
					    <trans-label slot="label" class="label" key="HS_Base" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="HS_Base" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('LE_LightDistribution_1', 'F')}">
					    <trans-label slot="label" class="label" key="LE_LightDistribution_1" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="LE_LightDistribution_1" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('EB_EBlockOperatingGearDescription.ElectronicConverter', 'D')}">
					    <trans-label slot="label" class="label" key="EB_EBlockOperatingGearDescription.ElectronicConverter" context="D"></trans-label>
					    <trans-label slot="value" class="value" key="EB_EBlockOperatingGearDescription.ElectronicConverter" context="DV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('U_TechnicalRegion.220-240V 50/60 Hz', 'D')}">
					    <trans-label slot="label" class="label" key="U_TechnicalRegion.220-240V 50/60 Hz" context="D"></trans-label>
					    <trans-label slot="value" class="value" key="U_TechnicalRegion.220-240V 50/60 Hz" context="DV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('HS_HousingColour', 'F')}">
					    <trans-label slot="label" class="label" key="HS_HousingColour" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="HS_HousingColour" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('HS_HousingCoating', 'F')}">
					    <trans-label slot="label" class="label" key="HS_HousingCoating" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="HS_HousingCoating" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t('HS_HousingPowerConnection', 'F')}">
					    <trans-label slot="label" class="label" key="HS_HousingPowerConnection" context="F"></trans-label>
					    <trans-label slot="value" class="value" key="HS_HousingPowerConnection" context="FV.S"></trans-label>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${isDimensionsPresent}">
					    <trans-label slot="label" class="label" key="TE_Dimensions" context="D"></trans-label>
					    <span slot="value" class="value">
					        ${dimensionDiameter ? html`${dimensionDiameter}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
					        ${dimensionDepth ? html`${dimensionDepth}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
					        ${dimensionWidth ? html`${dimensionWidth}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
					        ${dimensionHeight ? html`${dimensionHeight}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>` : ''}
					    </span>
					</shared-specsheet-feature>


					<shared-specsheet-feature class="${this.lineChoice}" condition="${isStandardsPresent}">
					    <trans-label slot="label" class="label" key="LU_ApplicableStandards" context="D"></trans-label>
					    <span slot="value" class="value">
					        ${aggregatedStandards}
					    </span>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${isLightEngineDescriptionPresent}">
					<trans-label slot="label" class="label" key="LE_LightEngineDescription" context="D"></trans-label>
					<span slot="value" class="value">
						${filterAndJoin([lightEngineDescription, opticalVersion, reflectorColour], ', ')}
					</span>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${descAtt}">
					<trans-label slot="label" class="label" key="LE_CRI" context="D"></trans-label>
					<span slot="value" class="value">${descAtt} W</span>
					</shared-specsheet-feature>

					<shared-specsheet-feature class="${this.lineChoice}" condition="${wattageMax}">
					<trans-label slot="label" class="label" key="LU_SystemWattage" context="D"></trans-label>
					<span slot="value" class="value">${wattageMax} W</span>
					</shared-specsheet-feature>
		 `;
	}
}
